/**
 * Exposure component module.
 * @module shared/components/Exposure
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Pie from '../components/graphs/Pie';
import {_openModal} from '../reducers/modals';
import {connect} from 'react-redux';
import useSWR from 'swr';
import {ColorBox} from '../utils/colors';
import {Column, Dropdown, LoadingSpinner} from '../index';
import Row from '../components/Row';
import pluralize from 'pluralize';
import {formatter} from '../utils/utils';
import Geographical from '../components/graphs/Geographical';
import useQueryObject from '../utils/useQueryObject';

const percentFormatter = formatter('number', {decimalPlaces: 1});

const exposureTitle = row => <><ColorBox color={row.color}/>{row.name}</>;

function formatExposureCategory(category) {
  if (!category) {
    return '';
  }

  return category
    .replace(/_+/g, ' ')
    .replace(/\w+/g, function capitalize(c) {
      return c.charAt(0).toUpperCase() + c.substr(1).toLowerCase();
    });
}

const ExposureGraph = ({session, cat, catID, sliceAction, centerAction, useInvestorPreference}) => {
  const {data: geographical} = useSWR('/exposures/geographical');
  const {data} = useSWR(cat && catID && `/${cat}/${catID}/exposures`);
  const {data: prefData, isValidating: validatingPrefs} = useSWR(useInvestorPreference && session.profile.id && `/preferences/users/${session.profile.id}/panels/investorGeneralWeb`);
  const prefCollection = (prefData && prefData.collections || []).find(v => v.title === 'investorExposureWeb');
  const prefGroup = (prefCollection && prefCollection.groups || []).find(v => v.title === 'investorExposureWeb');
  const prefs = (prefGroup && prefGroup.cores || []).find(v => v.title === 'investorDefaultExposureCategory');
  const prefOptions = prefs && prefs.options;
  const prefVal = prefs && prefs.val;
  const dataOptions = data && data.filter(r => !!(r.types?.length)).filter(r => !!r.category).map(c => {
    const label = c.displayName || formatExposureCategory(c.category);
    return {
      label: label,
      value: c.category,
      prefID: (prefOptions || []).find(v => v.displayName === label)?.id,
    };
  });
  const [category, setCategory] = useQueryObject('exposureCategory', {});
  const [regionSelector, setRegionSelector] = React.useState({label: 'All Regions', value: 'All Regions'});

  const buildType = d => ({
    id: d.id,
    ident: d.id,
    name: d.typeName,
    color: d.color[0] === '#' ? d.color : '#' + d.color,
    value: d.typeValue,
    y: d.typePercent * 100,
  });

  const regions = React.useMemo(() => data ? ([{typeName: 'All Regions'}, ...data.find(d => d.category === 'REGION').types])
    .map(r => ({label: r.typeName, value: r.typeName})) : [], [data]);

  const formattedData = React.useMemo(() => {
    if (data) {
      const c = data.find(d => d.category === (category && category.value || data[0].category));
      if (c.types.length) {
        if (category && category.value === 'COUNTRY' && regionSelector.value !== 'All Regions') {
          return c.types.filter(d => d.regionName === regionSelector.value).map(buildType);
        }
        return c.types.map(buildType);
      }
      return [
        {
          name: 'No ' + (category && pluralize(formatExposureCategory(category.value)) || 'Exposure'),
          color: '#797B83',
          y: 100,
        },
      ];
    }
  }, [data, category, regionSelector]);

  React.useEffect(() => {
    if (data && (prefData || !useInvestorPreference) && !validatingPrefs) {
      if (!category?.value || !data.find(r => r.category === category.value)) {
        const initialValue = (dataOptions || []).find(v => v.prefID === prefVal);
        const ca = dataOptions.filter(r => !!r.value)[0];
        if (!useInvestorPreference && ca?.value !== category?.value) {
          setCategory(ca);
        }
        if (useInvestorPreference && (category?.prefID !== initialValue?.value || !category?.prefID)) {
          setCategory(initialValue || ca);
        }
      }
    }
  }, [data, prefData, validatingPrefs]);

  const graphData = React.useMemo(() => category
    && ['COUNTRY', 'REGION'].includes(category.value)
    && formattedData
    && geographical
    && geographical.map(d => {
      const v = {...d};
      const o = formattedData.find(e => e.name === v.name || e.name === v.regionName);
      if (o) {
        v.ident = o.id;
        v.value = o.value;
        v.color = o.color;
        v.borderColor = v.color;
        v.y = o.y;
      } else {
        v.color = '#DADADA';
        v.borderColor = 'black';
        v.y = 0;
      }
      return v;
    }).filter(i => !!i.value), [formattedData, geographical, category, regionSelector]);

  if (!formattedData) {
    return <LoadingSpinner/>;
  }

  return <Row height={'100px'}
              alignSubRow={'stretch'}
              alignItems={'stretch'}
              layout={{grow: 1}}>
    <Column justifySubColumn={'top'}>
      <Row>
        <Dropdown dataPoint={{
          name: 'cat',
          value: category,
        }}
                  isClearable={false}
                  isSearchable={false}
                  onChange={(name, value) => setCategory(value)}
                  title={'Exposure Category'}
                  options={dataOptions}
                  placeholder={'Exposure Category'}/>
        {category && category.value === 'COUNTRY'
      && <Dropdown title={'Region'}
                   dataPoint={{name: 'regionSelector', value: regionSelector}}
                   onChange={(n, v) => setRegionSelector(v)}
                   isSearchable={false}
                   isClearable={false}
                   options={regions}/>}
      </Row>
      {(!category || !['COUNTRY', 'REGION'].includes(category.value))
      && <Column layout={{grow: 1}}>
        <Pie data={formattedData}
             alignTop={true}
             sliceAction={slice => sliceAction && sliceAction(slice, category, formattedData)}
             centerAction={centerAction && (() => centerAction(category, formattedData))}
             table={{
               defaultSort: {
                 column: cat !== 'models' ? 'value' : 'y',
                 desc: true,
               },
               columns: [
                 {
                   title: category && category.label || formatExposureCategory(data[0].category),
                   dataPoint: 'name',
                   type: 'text',
                   render: exposureTitle,
                   minWidth: 120,
                   isSortable: true,
                   verticalAlign: true,
                 },
                 cat !== 'models' && {title: 'Value', dataPoint: 'value', type: 'currency', minWidth: 100, isSortable: true},
                 {
                   title: 'Percentage',
                   dataPoint: 'y',
                   type: 'number',
                   render: row => `${Math.round(row.y * 10) / 10}%`,
                   minWidth: 90,
                   isSortable: true,
                 },
               ].filter(c => !!c),
             }}
             toolTipFormatter={function toolTipFormatter() {
               const p = this.point || this.points[0];
               if (['No Asset Classes', 'No Sectors'].indexOf(p.name) < 0) {
                 return '<div>'
                    + p.name.substr(0, 50)
                    + ': </div><div style="font-size:13px;margin-top:3px;" class="text-center">'
                    + percentFormatter(p.y)
                    + '%</div>';
               }
               return '<div>' + p.name.substr(0, 50) + '</div>';
             }}/>
      </Column>}
      {category && ['COUNTRY', 'REGION'].includes(category.value)
      && <Column layout={{grow: 1}}>
        <Geographical data={graphData}
                      formattedData={formattedData}
                      key={category.value }
                      table={{
                        defaultSort: {
                          column: cat !== 'models' ? 'value' : 'y',
                          desc: true,
                        },
                        columns: [
                          {
                            title: category && category.label || formatExposureCategory(data[0].category),
                            dataPoint: 'name',
                            type: 'text',
                            render: exposureTitle,
                            minWidth: 120,
                            isSortable: true,
                            verticalAlign: true,
                          },
                          cat !== 'models' && {
                            title: 'Value',
                            dataPoint: 'value',
                            type: 'currency',
                            minWidth: 100,
                            isSortable: true,
                          },
                          {
                            title: 'Percentage',
                            dataPoint: 'y',
                            type: 'number',
                            render: row => `${Math.round(row.y * 10) / 10}%`,
                            minWidth: 90,
                            isSortable: true,
                          },
                        ].filter(c => !!c),
                      }}
                      alignTop={true}
                      sliceAction={slice => sliceAction && sliceAction(slice, category, formattedData)}
                      toolTipFormatter={function toolTipFormatter() {
                        const p = this.point || this.points[0];
                        if (['No Asset Classes', 'No Sectors'].indexOf(p.name) < 0) {
                          return '<div>'
                            + p.name.substr(0, 50)
                            + ': </div><div style="font-size:13px;margin-top:3px;" class="text-center">'
                            + percentFormatter(p.y)
                            + '%</div>';
                        }
                        return '<div>' + p.name.substr(0, 50) + '</div>';
                      }}/>
      </Column>}
    </Column>
  </Row>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ExposureGraph);

ExposureGraph.propTypes = {
  session: PropTypes.object,
  openModal: PropTypes.func,
  cat: PropTypes.string.isRequired,
  catID: PropTypes.number.isRequired,
  sliceAction: PropTypes.func,
  centerAction: PropTypes.func,
  useInvestorPreference: PropTypes.bool,
};
